import React, { useRef, useEffect, useState } from "react";
import { TextB, TextItalic, TextUnderline, Highlighter, ListBullets, ArrowUUpLeft, ArrowUUpRight, Code } from "@phosphor-icons/react";

export default function RichStoryEditor({ value, onChange, placeholder = "Share your story... Use the toolbar to add bold, italic, highlights, lists, or paste raw HTML via the <> button." }) {
  const ref = useRef(null);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlDraft, setHtmlDraft] = useState(value || "");

  useEffect(() => {
    if (!htmlMode && ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    if (htmlMode) setHtmlDraft(value || "");
  }, [value, htmlMode]); // eslint-disable-line

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const wrapHighlight = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const mark = document.createElement("mark");
    mark.style.background = "#FFF3B0";
    mark.appendChild(range.extractContents());
    range.insertNode(mark);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const toggleHtml = () => {
    if (htmlMode) {
      // Switching BACK to visual: push the edited HTML to the canonical value
      onChange(htmlDraft);
    } else {
      setHtmlDraft(value || "");
    }
    setHtmlMode(!htmlMode);
  };

  const btnCls = "p-2 rounded-lg hover:bg-[#EEF4E9] text-[#1F2A1C]";

  return (
    <div className="border border-[#DCE5D4] rounded-xl bg-white overflow-hidden" data-testid="rich-story-editor">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-[#DCE5D4] bg-[#FBF9F6] flex-wrap">
        <button type="button" disabled={htmlMode} onClick={() => exec("bold")} className={btnCls + (htmlMode ? " opacity-40 cursor-not-allowed" : "")} title="Bold"><TextB size={18} weight="bold" /></button>
        <button type="button" disabled={htmlMode} onClick={() => exec("italic")} className={btnCls + (htmlMode ? " opacity-40 cursor-not-allowed" : "")} title="Italic"><TextItalic size={18} /></button>
        <button type="button" disabled={htmlMode} onClick={() => exec("underline")} className={btnCls + (htmlMode ? " opacity-40 cursor-not-allowed" : "")} title="Underline"><TextUnderline size={18} /></button>
        <button type="button" disabled={htmlMode} onClick={wrapHighlight} className={btnCls + (htmlMode ? " opacity-40 cursor-not-allowed" : "")} title="Highlight"><Highlighter size={18} weight="fill" /></button>
        <span className="w-px h-5 bg-[#DCE5D4] mx-1" />
        <button type="button" disabled={htmlMode} onClick={() => exec("insertUnorderedList")} className={btnCls + (htmlMode ? " opacity-40 cursor-not-allowed" : "")} title="Bullet list"><ListBullets size={18} /></button>
        <span className="w-px h-5 bg-[#DCE5D4] mx-1" />
        <select disabled={htmlMode} onChange={(e) => { exec("foreColor", e.target.value); e.target.value = ""; }} className={"text-xs bg-white border border-[#DCE5D4] rounded px-1 py-1" + (htmlMode ? " opacity-40 cursor-not-allowed" : "")} defaultValue="">
          <option value="" disabled>Color</option>
          <option value="#1F2A1C">Default</option>
          <option value="#4FAC39">Green</option>
          <option value="#B84A34">Red</option>
          <option value="#1F4FB8">Blue</option>
          <option value="#7A5E00">Gold</option>
        </select>
        <button type="button" onClick={toggleHtml} className={btnCls + " ml-auto " + (htmlMode ? "bg-[#EEF4E9] text-[#2F7A4A]" : "")} title={htmlMode ? "Back to visual editor" : "Edit / paste raw HTML"} data-testid="rich-story-html-toggle">
          <Code size={18} weight={htmlMode ? "fill" : "regular"} />
        </button>
        <button type="button" disabled={htmlMode} onClick={() => exec("undo")} className={btnCls + (htmlMode ? " opacity-40 cursor-not-allowed" : "")} title="Undo"><ArrowUUpLeft size={18} /></button>
        <button type="button" disabled={htmlMode} onClick={() => exec("redo")} className={btnCls + (htmlMode ? " opacity-40 cursor-not-allowed" : "")} title="Redo"><ArrowUUpRight size={18} /></button>
      </div>
      {htmlMode ? (
        <textarea
          value={htmlDraft}
          onChange={(e) => { setHtmlDraft(e.target.value); onChange(e.target.value); }}
          className="min-h-[220px] w-full p-4 outline-none text-xs leading-relaxed font-mono bg-[#FBF9F6]"
          placeholder="Paste or edit raw HTML here. Switch back to visual mode to see the rendered story."
          data-testid="rich-story-html-textarea"
        />
      ) : (
        <div
          ref={ref}
          contentEditable
          onInput={() => onChange(ref.current?.innerHTML || "")}
          className="min-h-[180px] p-4 outline-none text-sm leading-relaxed story-rich"
          suppressContentEditableWarning
          data-placeholder={placeholder}
        />
      )}
    </div>
  );
}
